/**
 * What a logged load means, as the workout's screens use it (`D209`, B6):
 * the unit the load field says, and the device's semantics for the figures
 * it draws itself (the live record, the after-workout volume).
 *
 * ── `loadUnitLabel` IS FOR OTHER SCREENS TOO ───────────────────────────────
 * The exercise sheet's "Como registrar" sentence (`D199`,
 * `features/exercise/how-to-log.ts`) QUOTES this function's words — "no
 * campo “kg por halter”" — rather than restating them, so the sheet and the
 * field cannot drift apart. It is the ONE place `loadMode` becomes a unit.
 */

import { profileKeys } from "@repo/client/data/profile";
import type { AppLocale } from "@repo/client/format";
import type { DeviceLoadSemantics } from "@repo/client/session";
import type { WeightUnit } from "@repo/contract";
import type { Laterality, LoadMode } from "@repo/contract/catalog";
import type { MeasurementListOutput } from "@repo/contract/training";
import {
  "workout-logging.unit_assisted" as unitAssisted,
  "workout-logging.unit_assisted_lb" as unitAssistedLb,
  "workout-logging.unit_bodyweight_plus" as unitBodyweightPlus,
  "workout-logging.unit_bodyweight_plus_lb" as unitBodyweightPlusLb,
  "workout-logging.unit_per_hand" as unitPerHand,
  "workout-logging.unit_per_hand_lb" as unitPerHandLb,
  "workout-logging.unit_reps_per_side" as unitRepsPerSide,
  "workout-logging.unit_total" as unitTotal,
  "workout-logging.unit_total_lb" as unitTotalLb,
} from "@repo/i18n";
import type { QueryClient } from "@tanstack/react-query";
import { weightUnit } from "../../stores/weight-unit";
import type { WorkoutSession } from "./store/session-model";

/**
 * "kg", "kg por halter", "kg extra" or "kg de assistência" — or their pound
 * twins in the person's unit (`D266`). `locale` and `unit` are for a caller
 * outside React (a test); a screen omits them and reads the app's.
 */
export function loadUnitLabel(
  loadMode: LoadMode | null | undefined,
  locale?: AppLocale,
  unit: WeightUnit = weightUnit()
): string {
  const options = locale === undefined ? undefined : { locale };
  const lb = unit === "lb";
  switch (loadMode) {
    case "per_hand":
      return (lb ? unitPerHandLb : unitPerHand)({}, options);
    case "bodyweight_plus":
      return (lb ? unitBodyweightPlusLb : unitBodyweightPlus)({}, options);
    case "assisted":
      return (lb ? unitAssistedLb : unitAssisted)({}, options);
    default:
      return (lb ? unitTotalLb : unitTotal)({}, options);
  }
}

/**
 * What the reps field says under its number (`D232`): "por lado" for a
 * `unilateral` exercise, whose reps are one side's, and nothing otherwise —
 * the field's label already says "reps". `how-to-log.ts` quotes it, as it
 * quotes {@link loadUnitLabel}.
 */
export function repsUnitLabel(
  laterality: Laterality | null | undefined,
  locale?: AppLocale
): string {
  return laterality === "unilateral"
    ? unitRepsPerSide({}, locale === undefined ? undefined : { locale })
    : "";
}

/** A device-side look-up of an exercise's mode: the catalogue copy's. */
export type LoadModeLookup = (
  exerciseId: string
) => LoadMode | null | undefined;

/**
 * The mode of an exercise of this session: the plan item's (from the plan
 * read), else the catalogue copy's — which is where a today-only swap's
 * comes from, since a swap candidate carries no mode — else `total`.
 */
export function loadModeOfSession(
  session: Pick<WorkoutSession, "items">,
  exerciseId: string,
  lookup: LoadModeLookup
): LoadMode | null {
  const item = session.items.find((each) => each.exerciseId === exerciseId);
  if (item?.exercise.loadMode !== undefined) {
    return item.exercise.loadMode;
  }
  return lookup(exerciseId) ?? null;
}

/** A device-side look-up of an exercise's laterality (`D232`). */
export type LateralityLookup = (
  exerciseId: string
) => Laterality | null | undefined;

/**
 * The laterality of an exercise of this session, found as its mode is: the
 * plan item's, else the catalogue copy's (a swap carries none), else unknown.
 */
export function lateralityOfSession(
  session: Pick<WorkoutSession, "items">,
  exerciseId: string,
  lookup: LateralityLookup
): Laterality | null {
  const item = session.items.find((each) => each.exerciseId === exerciseId);
  if (
    item?.exercise.laterality !== undefined &&
    item.exercise.laterality !== null
  ) {
    return item.exercise.laterality;
  }
  return lookup(exerciseId) ?? null;
}

/**
 * The latest kilogram weigh-in the Query cache holds, from any weight series
 * read (Progress, the profile), or `null`. Offline it is what was last seen.
 */
export function latestCachedWeightKg(client: QueryClient): number | null {
  let latest: { at: number; kg: number } | null = null;
  for (const [, data] of client.getQueriesData<MeasurementListOutput>({
    queryKey: profileKeys.measurementsAll,
  })) {
    const row = data?.latest;
    if (data?.kind !== "weight" || row === null || row === undefined) {
      continue;
    }
    const at = Date.parse(row.measuredAt);
    if (row.unit === "kg" && (latest === null || at > latest.at)) {
      latest = { at, kg: row.value };
    }
  }
  return latest?.kg ?? null;
}

/** The session's semantics for the device's own figures. */
export function deviceSemanticsOf(
  session: Pick<WorkoutSession, "items">,
  bodyWeightKg: number | null,
  lookup: LoadModeLookup,
  /** `D232`; omitted reads every exercise as both sides together. */
  sides: LateralityLookup = () => null
): DeviceLoadSemantics {
  return {
    bodyWeightKg,
    lateralityOf: (exerciseId) =>
      lateralityOfSession(session, exerciseId, sides),
    loadModeOf: (exerciseId) => loadModeOfSession(session, exerciseId, lookup),
  };
}
